import type * as echarts from "echarts/types/dist/echarts";

import type { ChartVizSpec, QuerySpec } from "@/lib/reports/schema";
import type { QueryResult } from "@/lib/reports/adapters/types";

/** Alias so the per-chart builders don't each re-import the echarts types. */
export type ChartOption = echarts.EChartsCoreOption;

/**
 * Shared context handed to every per-type chart builder. Bundles the spec
 * inputs + the precomputed key/measure locals + the base option so each
 * builder produces a byte-identical option to the inline case it replaced.
 */
export interface ChartBuildCtx {
  viz: ChartVizSpec;
  query: QuerySpec;
  data: QueryResult;
  palette: string[];
  base: ChartOption;
  measure: string;
  dimension: string | undefined;
  time: string | undefined;
  xKey: string;
  yKey: string;
}

/** A per-type builder: ctx → fully-formed ECharts option. */
export type ChartBuilder = (ctx: ChartBuildCtx) => ChartOption;

/** The shared base option every builder spreads over. Lives in one place so
 *  the defaults stay consistent across chart types. */
export function buildBaseOption(palette: string[]): ChartOption {
  return {
    color: palette,
    // Compact grid — axis names are intentionally stripped (the block
    // title already conveys what each axis means; redundant axis names
    // overlap labels at every breakpoint). Right padding generous enough
    // that the rightmost x-axis tick (e.g. "2026-05-15") isn't clipped.
    grid: { left: 24, right: 48, top: 16, bottom: 28, containLabel: true },
    tooltip: {
      trigger: "item",
      confine: true,
      // Branded card — soft border, generous radius, layered shadow so it
      // reads as elevated over the chart. Inherits the body font.
      borderColor: "rgba(17,24,39,0.08)",
      borderRadius: 8,
      padding: [8, 12],
      backgroundColor: "rgba(255,255,255,0.98)",
      extraCssText: "box-shadow: 0 12px 32px -8px rgba(17,24,39,0.18), 0 2px 6px -1px rgba(17,24,39,0.08); backdrop-filter: saturate(140%) blur(4px);",
      textStyle: { color: "#111", fontSize: 12, fontFamily: "Inter, sans-serif" },
    },
    legend: {
      // Defaults that every legend instance below merges with: legends
      // are clickable in ECharts (toggles series visibility) — make
      // that clear with a pointer cursor + hover tint.
      show: false,
      selectedMode: true,
      inactiveColor: "#cbd5e1",
      icon: "roundRect",
      itemGap: 14,
    },
    animationDuration: 300,
    textStyle: { fontFamily: "Inter, sans-serif" },
  };
}
